/**
 * A submitted post must come from an account this creator already connected,
 * on a platform the campaign asked for. The URL handle is compared to the
 * connected handle when the grammar includes one.
 */
import {
  extractHandleFromUrl, handlesMatch, normalizeHandle,
} from "./platforms/handles";

export const ACTIVE_CONNECTION_STATES = ["CONNECTED", "EXPIRING"] as const;

export interface ConnectedAccount {
  platform: string;
  handle: string | null;
  connection_state: string;
}

export type OwnershipRefusal = {
  ok: false;
  code: "PLATFORM_NOT_REQUIRED" | "PLATFORM_NOT_CONNECTED" | "HANDLE_MISSING" | "HANDLE_MISMATCH";
  message: string;
  field?: string;
};

export type OwnershipResult = { ok: true } | OwnershipRefusal;

const PLATFORM_LABEL: Record<string, string> = {
  youtube: "YouTube",
  tiktok: "TikTok",
  instagram: "Instagram",
  facebook: "Facebook",
  reddit: "Reddit",
};

function labelOf(id: string): string {
  return PLATFORM_LABEL[id] ?? id.replaceAll("_", " ");
}

export function isActiveConnection(state: string | null | undefined): boolean {
  return (ACTIVE_CONNECTION_STATES as readonly string[]).includes(state ?? "");
}

export function requiredSocialPlatforms(required: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of required) {
    const p = raw.trim().toLowerCase();
    if (!p || p === "sandbox" || seen.has(p)) continue;
    seen.add(p);
    out.push(p);
  }
  return out;
}

export function missingRequiredPlatforms(
  required: readonly string[],
  connections: readonly ConnectedAccount[],
): string[] {
  const connected = new Set(
    connections
      .filter((c) => isActiveConnection(c.connection_state))
      .map((c) => c.platform.toLowerCase()),
  );
  return requiredSocialPlatforms(required).filter((p) => !connected.has(p));
}

export interface PlatformSubmitStatus {
  id: string;
  connected: boolean;
  submitted: number;
  limit: number;
  canSubmit: boolean;
}

/** One submit slot per required platform. Missing accounts do not block the rest. */
export function platformSubmitStatuses(input: {
  required: readonly string[];
  connections: readonly ConnectedAccount[];
  submissions?: readonly { platform?: string | null; state?: string | null }[];
  limit?: number;
}): PlatformSubmitStatus[] {
  const limit = Number.isInteger(input.limit) && (input.limit ?? 0) > 0 ? Number(input.limit) : 1;
  const connected = new Set(
    input.connections
      .filter((c) => isActiveConnection(c.connection_state))
      .map((c) => c.platform.toLowerCase()),
  );
  const used = new Map<string, number>();
  for (const row of input.submissions ?? []) {
    if ((row.state ?? "").toUpperCase() === "WITHDRAWN") continue;
    const p = (row.platform ?? "").trim().toLowerCase();
    if (!p) continue;
    used.set(p, (used.get(p) ?? 0) + 1);
  }
  return requiredSocialPlatforms(input.required).map((id) => {
    const submitted = used.get(id) ?? 0;
    const isConnected = connected.has(id);
    return {
      id,
      connected: isConnected,
      submitted,
      limit,
      canSubmit: isConnected && submitted < limit,
    };
  });
}

export function checkSubmissionOwnership(opts: {
  platform: string;
  url: string;
  required: readonly string[];
  connections: readonly ConnectedAccount[];
}): OwnershipResult {
  const platform = opts.platform.toLowerCase();
  const required = opts.required.map((p) => p.toLowerCase()).filter((p) => p && p !== "sandbox");
  if (required.length > 0 && !required.includes(platform)) {
    const names = required.map(labelOf).join(" or ");
    return {
      ok: false,
      code: "PLATFORM_NOT_REQUIRED",
      field: "url",
      message: `This campaign asks for a ${names} post. Submit a link from that account.`,
    };
  }

  const connected = opts.connections.find((c) =>
    c.platform.toLowerCase() === platform && isActiveConnection(c.connection_state));
  if (!connected) {
    return {
      ok: false,
      code: "PLATFORM_NOT_CONNECTED",
      field: "url",
      message: `Connect your ${labelOf(platform)} account before you submit a post from it.`,
    };
  }

  // YouTube watch links, short TikTok links, Instagram reels and the
  // like do not name the account. Those stay connection-only so the
  // brand can compare the connected handle with the pasted URL.
  const urlHandle = extractHandleFromUrl(opts.url);
  if (urlHandle && !handlesMatch(urlHandle, connected.handle)) {
    const named = normalizeHandle(connected.handle);
    return {
      ok: false,
      code: "HANDLE_MISMATCH",
      field: "url",
      message: named
        ? `That link is not from your connected ${labelOf(platform)} account @${named}.`
        : `That link is not from the ${labelOf(platform)} account you connected.`,
    };
  }

  return { ok: true };
}
